// Port of hec-composable/hec/builder/blocks/tpa-balance/utils.js
// Mock data + Builder input subField schemas for the TPABalance block.

const getDateWithinExpiryWindow = (daysFromNow = 30) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString();
};

// `balance` is a getter so nearExpiryDate is computed fresh on each access —
// prevents stale dates in long-lived Worker isolates.
export const tpaBalanceMockData = {
  get balance() {
    const nearExpiryDate = getDateWithinExpiryWindow(30);
    return {
      tracker: {
        warning: false,
        usedSessionBalance: "2500.00",
        currentAvailableBalance: "12984.02",
        nearestExpiryBalanceObj: {
          nearestExpiryBalance: 6971.02,
          nearestExpiryDate: nearExpiryDate,
        },
        balanceUsagePercentage: "19.25",
        leftoverOrderAmount: "45.99",
      },
      popup: [
        {
          accountName: "Health Care FSA",
          balance: 6971.02,
          expiryDate: nearExpiryDate,
          carryoverBalance: 650,
        },
        {
          accountName: "Health Care FSA",
          balance: 2137,
          expiryDate: "2025-12-31T00:00:00.000Z",
          carryoverBalance: 650,
        },
        {
          accountName: "Health Care HSA",
          balance: 2321,
          carryoverBalance: 650,
        },
        {
          accountName: "Health Care HRA",
          balance: 1555,
          expiryDate: "2025-12-31T00:00:00.000Z",
          carryoverBalance: 650,
        },
      ],
    };
  },
};

type MockData = typeof tpaBalanceMockData;

// Deep-merges partial Builder-authored mock data over canonical defaults so a
// partially-filled entry still shows complete tracker + popup in the editor.
export function mergeMockDataWithDefaults(
  partial?: MockData,
): MockData["balance"] {
  const defaults = tpaBalanceMockData.balance;
  if (!partial?.balance) return defaults;
  return {
    tracker: { ...defaults.tracker, ...partial.balance.tracker },
    popup: partial.balance.popup ?? defaults.popup,
  };
}

export const trackerMockDataSubFields = [
  {
    name: "tracker",
    type: "object",
    subFields: [
      { name: "warning", type: "boolean", required: true, defaultValue: false },
      {
        name: "usedSessionBalance",
        type: "string",
        required: true,
        defaultValue: "2500.00",
      },
      {
        name: "currentAvailableBalance",
        type: "string",
        required: true,
        defaultValue: "12984.02",
      },
      {
        name: "nearestExpiryBalanceObj",
        type: "object",
        subFields: [
          {
            name: "nearestExpiryBalance",
            type: "number",
            required: false,
            defaultValue: 6971.02,
          },
          {
            name: "nearestExpiryDate",
            type: "string",
            required: false,
            defaultValue: getDateWithinExpiryWindow(30),
          },
        ],
      },
      {
        name: "balanceUsagePercentage",
        type: "string",
        required: true,
        defaultValue: "19.25",
      },
      {
        name: "leftoverOrderAmount",
        type: "string",
        defaultValue: "45.99",
      },
    ],
  },
  {
    name: "popup",
    type: "list",
    required: true,
    defaultValue: [
      {
        accountName: "Health Care FSA",
        balance: 6971.02,
        expiryDate: getDateWithinExpiryWindow(30),
        carryoverBalance: 650,
      },
      {
        accountName: "Health Care FSA",
        balance: 2137,
        expiryDate: "2025-12-31T00:00:00.000Z",
        carryoverBalance: 650,
      },
      {
        accountName: "Health Care HSA",
        balance: 2321,
        carryoverBalance: 650,
      },
      {
        accountName: "Health Care HRA",
        balance: 1555,
        expiryDate: "2025-12-31T00:00:00.000Z",
        carryoverBalance: 650,
      },
    ],
    subFields: [
      {
        name: "accountName",
        type: "string",
        required: true,
        defaultValue: "Health Care FSA",
      },
      { name: "balance", type: "number", required: true, defaultValue: 0 },
      { name: "expiryDate", type: "string" },
      { name: "carryoverBalance", type: "number", defaultValue: 0 },
    ],
  },
];
